import { Alert, DatePicker, Select, Space, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/shared/components/DataTable";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useEmployeeNames, withSelectedEmployeeOption } from "@/shared/hooks/useEmployeeNames";
import { useEmployeeOptions, useShiftInstancesList } from "../hooks/useSchedules";
import type { ShiftInstance } from "../types";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_RANGE_DAYS = 30;
const EMPLOYEE_SEARCH_DEBOUNCE_MS = 300;

/** ISO timestamptz → mahalliy "DD.MM.YYYY HH:mm". */
function formatMoment(value: string): string {
	return dayjs(value).format("DD.MM.YYYY HH:mm");
}

export function ShiftInstancesPage() {
	const { t } = useTranslation("schedules");
	const [range, setRange] = useState<[Dayjs, Dayjs]>([
		dayjs(),
		dayjs().add(DEFAULT_RANGE_DAYS, "day"),
	]);
	const [employeeId, setEmployeeId] = useState<string | undefined>(undefined);
	const [employeeSearch, setEmployeeSearch] = useState("");
	const debouncedEmployeeSearch = useDebouncedValue(employeeSearch, EMPLOYEE_SEARCH_DEBOUNCE_MS);

	const { data: employees = [], isFetching: isEmployeeSearchLoading } =
		useEmployeeOptions(debouncedEmployeeSearch);

	const query = useShiftInstancesList({
		from: range[0].format("YYYY-MM-DD"),
		to: range[1].format("YYYY-MM-DD"),
		employeeId,
	});

	// ⚠️ Javobda xodim ismi YO'Q (sof `ShiftInstanceRecord`), shuning uchun nom
	// mijozda xaritalanadi — lekin FILTR Select'ining opsiyalaridan emas,
	// ko'rsatilayotgan QATORLARDAN (`useEmployeeNames` izohiga qarang).
	const employeeNames = useEmployeeNames([...query.items.map((row) => row.employeeId), employeeId]);

	const columns: ColumnsType<ShiftInstance> = [
		{ title: t("shiftInstancesPage.columns.workDate"), dataIndex: "workDate", key: "workDate" },
		{
			title: t("shiftInstancesPage.columns.employee"),
			key: "employeeId",
			// Topilmasa ID ko'rsatiladi: «—» qo'yish qatorni tekshirib
			// bo'lmaydigan holga keltirardi.
			render: (_, row) => employeeNames.get(row.employeeId) ?? row.employeeId,
		},
		{
			title: t("shiftInstancesPage.columns.plannedStart"),
			dataIndex: "plannedStartAt",
			key: "plannedStartAt",
			render: (value: string) => formatMoment(value),
		},
		{
			title: t("shiftInstancesPage.columns.plannedEnd"),
			dataIndex: "plannedEndAt",
			key: "plannedEndAt",
			render: (value: string) => formatMoment(value),
		},
	];

	return (
		<div>
			<Title level={3} className="mb-6">
				{t("shiftInstancesPage.title")}
			</Title>

			<Alert
				className="mb-4"
				type="info"
				showIcon
				title={t("shiftInstancesPage.info.title")}
				description={t("shiftInstancesPage.info.description")}
			/>

			<Space wrap className="mb-4">
				<RangePicker
					value={range}
					allowClear={false}
					onChange={(value) => {
						if (value?.[0] && value[1]) {
							setRange([value[0], value[1]]);
							query.setPage(1);
						}
					}}
				/>
				<Select
					allowClear
					placeholder={t("shiftInstancesPage.employeePlaceholder")}
					style={{ width: 240 }}
					options={withSelectedEmployeeOption(
						employees.map((employee) => ({
							value: employee.id,
							label: employee.fullName,
						})),
						employeeId,
						employeeNames
					)}
					value={employeeId}
					showSearch={{ filterOption: false, onSearch: setEmployeeSearch }}
					loading={isEmployeeSearchLoading}
					notFoundContent={
						isEmployeeSearchLoading ? t("employeeSelect.searching") : t("employeeSelect.notFound")
					}
					onChange={(value) => {
						setEmployeeId(value);
						query.setPage(1);
					}}
				/>
			</Space>

			<DataTable<ShiftInstance> columns={columns} query={query} />
		</div>
	);
}
